import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BsChevronDown } from "react-icons/bs";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { useToast } from "../../common/hooks/useToast";
import { Popover } from "../../common/popover/Popover";
import { formatRetryAfter, getRateLimitRejection } from "../../common/rateLimit";
import { Item } from "../../styles/common";
import { CountryBlockTable } from "./CountryBlockTable";
import { COUNTRY_BLOCK_LIST } from "./countryBlocking.queries";
import { SET_COUNTRY_BLOCKED } from "./countryBlocking.mutation";
import {
  CountryBlockList,
  CountryBlockStatus,
  EMPTY_COUNTRY_FILTER,
  OTP_SEND_LOG_SQL,
  PROPAGATION_NOTICE,
  PendingPatch,
  applyPatches,
  continentsOf,
  describeToggleFailure,
  filterCountries,
  formatTimestamp,
  isIpOnlyBlock,
  summarizeToggle,
  wasMigrated,
} from "./countryBlock";

export const CountryBlocking: React.FC = (): JSX.Element => {
  /** Loaded once on open, refreshed on the explicit button. Never on a timer. */
  const { data, loading, error, refetch } = useQuery(COUNTRY_BLOCK_LIST, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [setCountryBlocked] = useMutation(SET_COUNTRY_BLOCKED);

  const toast = useToast();

  const [filter, setFilter] = useState(EMPTY_COUNTRY_FILTER);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<CountryBlockStatus | null>(
    null
  );

  /**
   * What we last successfully wrote, by country code. Preferred over the
   * server's value for up to the propagation window, because a refetch can be
   * served by an instance whose 30s cache still holds the old list and the
   * switch would snap back.
   */
  const [patches, setPatches] = useState<Map<string, PendingPatch>>(new Map());

  const list: CountryBlockList | undefined = data?.countryBlockList;

  const countries = useMemo(
    () => applyPatches(list?.countries ?? [], patches),
    [list, patches]
  );

  const blocked = useMemo(
    () => countries.filter((country) => country.blocked),
    [countries]
  );

  /** Blocked countries whose numbers cannot be matched — half-enforced. */
  const ipOnly = useMemo(() => blocked.filter(isIpOnlyBlock), [blocked]);

  const visible = useMemo(
    () => filterCountries(countries, filter),
    [countries, filter]
  );

  const continents = useMemo(() => continentsOf(countries), [countries]);

  /** Blocks the merge migration wrote, which no admin here will recognise. */
  const migrated = useMemo(() => blocked.filter(wasMigrated), [blocked]);

  /**
   * Writes one country and patches the row from the result.
   *
   * Deliberately does not refetch: `setCountryBlocked` returns the authoritative
   * row, and a re-read can hit an instance that has not expired its cache yet,
   * which would report the pre-write value and look like the save failed.
   *
   * `blocked` is the state to set rather than a flip, so a retry after a
   * timeout cannot land the country in the opposite state.
   */
  const applyToggle = useCallback(
    async (country: CountryBlockStatus, next: boolean) => {
      setSavingCode(country.countryCode);

      try {
        const result = await setCountryBlocked({
          variables: { countryCode: country.countryCode, blocked: next },
        });

        const patch = result.data?.setCountryBlocked;

        if (!patch) {
          toast.error(`The server did not confirm the change to ${country.name}.`);
          return;
        }

        setPatches((previous) => {
          const updated = new Map(previous);
          updated.set(patch.countryCode, { patch, at: Date.now() });
          return updated;
        });

        // Says which half of the block actually took effect: a country with a
        // shared dialling code is blocked by IP only, and the toast is the last
        // moment an admin is looking when they find out.
        toast.success(summarizeToggle(patch));
      } catch (mutationError) {
        // A rate-limit rejection is already announced by the global Apollo
        // error link in the same words; toasting it here would double it.
        if (!getRateLimitRejection(mutationError)) {
          toast.error(describeToggleFailure(mutationError, country, next));
        }
      } finally {
        setSavingCode(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCountryBlocked]
  );

  /**
   * Blocking is confirmed, unblocking is not.
   *
   * Blocking silently refuses every OTP from a whole country and the people
   * caught by it see nothing but a code that never arrives, so a mis-tap in a
   * 251-row table is expensive and invisible. Unblocking is the permissive
   * direction — the backend allows it even for codes it does not recognise —
   * so putting a dialog in front of it would only slow down the fix.
   */
  const handleToggle = useCallback(
    (country: CountryBlockStatus, next: boolean) => {
      if (next) {
        setConfirmBlock(country);
        return;
      }

      applyToggle(country, false);
    },
    [applyToggle]
  );

  const queryRejection = getRateLimitRejection(error);

  if (loading && !data) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading countries…
        </Typography>
      </Stack>
    );
  }

  if (queryRejection) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Item>
          <Alert severity="warning" sx={{ m: 2 }}>
            <AlertTitle>This panel hit its own rate limit</AlertTitle>
            {queryRejection.message}
            {queryRejection.retryAfterSeconds !== undefined && (
              <> Try again in {formatRetryAfter(queryRejection.retryAfterSeconds)}.</>
            )}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            </Box>
          </Alert>
        </Item>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Item>
          <ErrorNotifier error={error} />
        </Item>
      </Box>
    );
  }

  if (!list) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
        No country list returned.
      </Typography>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        {/* ---------------------------------------------- Header */}
        <Grid item xs={12}>
          <Item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                rowGap={1}
                sx={{ m: 2 }}
              >
                <Typography variant="h4">Country blocking</Typography>

                {/*
                 * Counted off the full list rather than read from the payload's
                 * `blockedCount`: both ignore the search box, but this one also
                 * reflects a toggle the server has not propagated yet.
                 */}
                <Chip
                  color={blocked.length ? "error" : "success"}
                  label={
                    blocked.length
                      ? `${blocked.length} blocked of ${list.totalCount}`
                      : `Nothing blocked (${list.totalCount} countries)`
                  }
                />
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ m: 2 }}
              >
                {list.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Last changed {formatTimestamp(list.updatedAt)}
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  onClick={() => refetch()}
                >
                  {loading ? "Refreshing…" : "Refresh"}
                </Button>
              </Stack>
            </Grid>
          </Item>
        </Grid>

        {/* ---------------------------------------------- Stored-row warnings */}
        {list.warnings.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>The server flagged this list</AlertTitle>
              {list.warnings.map((warning) => (
                <Typography key={warning} variant="body2">
                  {warning}
                </Typography>
              ))}
              {/*
               * Deliberately conditional wording: warnings cover both a
               * corrupt stored row and a country whose dialling code is
               * shared, and asserting corruption for the second would be
               * wrong.
               */}
              <Typography variant="body2" sx={{ mt: 1 }}>
                If the row itself could not be parsed the list resolves to{" "}
                <strong>nothing blocked</strong> rather than failing, so the
                signup path is never taken down by a bad hand-edit.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* ---------------------------------------------- What this does */}
        <Grid item xs={12}>
          <Alert severity="info">
            <AlertTitle>What blocking does</AlertTitle>
            <Typography variant="body2">
              Blocking stops <strong>OTP requests only</strong> — it is signup
              spam control, not an access boundary. Existing accounts keep
              working, nobody is signed out, and the rest of the API is
              unaffected.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              A blocked country is caught on <strong>two signals</strong>, and
              either is enough: where the request came{" "}
              <strong>from</strong> (the caller's IP, geolocated) and where the
              code would go <strong>to</strong> (the number's dialling code).
              Each covers the other's hole — geo-IP is defeated by a VPN, and a
              phone prefix says nothing about where the person is.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The two fail differently. The IP block is{" "}
              <strong>silent</strong>: a normal-looking response and a code that
              never arrives, so the user reports a missing code rather than a
              block. The phone block returns a visible{" "}
              <code>PHONE_NOT_SUPPORTED</code>, so nobody retypes a legitimately
              blocked number forever believing they made a typo. Changes apply
              within a minute.
            </Typography>
          </Alert>
        </Grid>

        {/* ---------------------------------------------- Half-enforced blocks */}
        {ipOnly.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>
                {ipOnly.length === 1
                  ? "One blocked country is enforced by IP only"
                  : `${ipOnly.length} blocked countries are enforced by IP only`}
              </AlertTitle>
              <Typography variant="body2">
                {ipOnly.map((country) => country.name).join(", ")} share a
                dialling code with other countries, so a number cannot be
                attributed to them and only the IP check runs. Someone using one
                of those numbers from an IP that is not blocked is{" "}
                <strong>not stopped</strong>.
              </Typography>
              {/*
               * There is deliberately no workaround to offer here any more.
               * The raw-prefix list that could express a single area code by
               * hand was removed so country blocking has one home, and
               * splitting a shared code properly needs NANP area-code tables
               * on the server. Pointing an admin at a screen that cannot do it
               * would be worse than saying so.
               */}
              <Typography variant="body2" sx={{ mt: 1 }}>
                There is no second list to fall back on. Blocking one of these
                by number needs area-code support on the server — raise it as
                work rather than looking for it in the dashboard.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* ---------------------------------------------- Currently blocked */}
        <Grid item xs={12}>
          <Item sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Currently blocked
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The whole enforced list. Remove one to unblock it immediately — no
              confirmation, because unblocking is always the safe direction.
            </Typography>

            {/*
             * Without this the screen opens on countries nobody set. The old
             * raw-prefix mechanism was merged into this list, and its 14 live
             * prefixes came across as country blocks — they were already being
             * enforced, they were just invisible before.
             */}
            {migrated.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {migrated.length} of these were carried over from the old prefix
                list when the two mechanisms were merged. They were already
                blocking numbers before this screen existed — the change is that
                they are now visible, and that they block by IP as well.
              </Alert>
            )}

            {blocked.length ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {blocked.map((country) => (
                  <Chip
                    key={country.countryCode}
                    color={isIpOnlyBlock(country) ? "warning" : "error"}
                    variant="outlined"
                    label={
                      isIpOnlyBlock(country)
                        ? `${country.name} (${country.countryCode}) — IP only`
                        : `${country.name} (${country.countryCode}) · ${country.phoneCode}`
                    }
                    disabled={savingCode === country.countryCode}
                    onDelete={() => applyToggle(country, false)}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No country is blocked. Everyone can request an OTP.
              </Typography>
            )}
          </Item>
        </Grid>

        {/* ---------------------------------------------- Filters + table */}
        <Grid item xs={12}>
          <Item sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              rowGap={2}
              sx={{ mb: 2 }}
            >
              <TextField
                size="small"
                label="Search"
                placeholder="Name, PK or PAK"
                value={filter.search}
                onChange={(event) =>
                  setFilter((previous) => ({
                    ...previous,
                    search: event.target.value,
                  }))
                }
                sx={{ minWidth: 240 }}
              />

              <TextField
                select
                size="small"
                label="Continent"
                value={filter.continent}
                onChange={(event) =>
                  setFilter((previous) => ({
                    ...previous,
                    continent: event.target.value,
                  }))
                }
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All continents</MenuItem>
                {continents.map((continent) => (
                  <MenuItem key={continent} value={continent}>
                    {continent}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Switch
                    checked={filter.blockedOnly}
                    onChange={(event) =>
                      setFilter((previous) => ({
                        ...previous,
                        blockedOnly: event.target.checked,
                      }))
                    }
                  />
                }
                label="Blocked only"
              />

              <Tooltip title="The list is fetched once and filtered in the browser, so searching costs nothing.">
                <Typography variant="caption" color="text.secondary">
                  Showing {visible.length} of {countries.length}
                </Typography>
              </Tooltip>
            </Stack>

            <CountryBlockTable
              countries={visible}
              savingCode={savingCode}
              patches={patches}
              onToggle={handleToggle}
            />
          </Item>
        </Grid>

        {/* ---------------------------------------------- Troubleshooting */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<BsChevronDown />}>
              <Typography variant="h6">
                Someone says they never got their code
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                If they were caught by their number they saw{" "}
                <code>PHONE_NOT_SUPPORTED</code> and will say so. If they were
                caught by their IP address they saw nothing at all, and no
                amount of asking will distinguish that from a code that got
                lost. The send log is the only evidence, and its{" "}
                <code>outcome</code> names which list refused them —{" "}
                <code>blocked_prefix</code> is the prefix row on App Config, the
                other two are this screen:
              </Typography>

              <Box
                component="pre"
                sx={{
                  p: 2,
                  m: 0,
                  overflowX: "auto",
                  fontSize: "0.8rem",
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                }}
              >
                {OTP_SEND_LOG_SQL}
              </Box>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Reasons a legitimate user gets caught: geo-IP is approximate, so
                a VPN, a satellite link or a carrier routing through another
                country reads as the wrong country; someone travelling is
                blocked by where they are, not where their number is from; and
                someone using a blocked country's number is refused wherever
                they are. Numbers are matched however they were typed — spaces,
                <code>00</code> prefixes and non-Latin digits all normalise to
                the same number, so a block cannot be stepped around by
                reformatting.
              </Typography>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                The IP check <strong>fails open</strong> — if the geo-IP
                provider is slow or down the request is allowed through. The
                phone check needs no lookup and is unaffected. Either way this
                is spam control, not an access boundary.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* ---------------------------------------------- Block confirmation */}
      <Popover open={Boolean(confirmBlock)} handleClose={() => setConfirmBlock(null)}>
        {/*
         * `Item`, not `Box`: reactjs-popup's stylesheet is never imported, so
         * the popup wrapper is transparent — the content must bring its own
         * opaque surface or this reads straight through to the page behind it.
         */}
        <Item sx={{ p: 3, maxWidth: 480, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Block {confirmBlock?.name}?
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Nobody whose IP address geolocates to {confirmBlock?.name} will be
            able to request a verification code — including anyone travelling
            there and anyone on a VPN that exits there. They will see no error;
            the code simply never arrives.
          </Typography>

          {/*
           * Stated before the write, not after. Which half of the block will
           * actually run is decided by the country's dialling code, and an
           * admin blocking Jamaica to stop Jamaican numbers needs to know it
           * will not do that *while they can still cancel*.
           */}
          {confirmBlock?.phoneCode ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Codes to {confirmBlock.phoneCode} numbers will also be refused,
              wherever they are requested from, with a visible{" "}
              <code>PHONE_NOT_SUPPORTED</code> error. {PROPAGATION_NOTICE}
            </Typography>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {confirmBlock?.phoneBlockingNote ??
                  `${confirmBlock?.name}'s dialling code is shared with other countries, so its numbers cannot be matched.`}{" "}
                Numbers will keep working — only the IP half of this block will
                run. {PROPAGATION_NOTICE}
              </Typography>
            </Alert>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirmBlock(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                const country = confirmBlock;
                setConfirmBlock(null);
                if (country) applyToggle(country, true);
              }}
            >
              Block {confirmBlock?.countryCode}
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Box>
  );
};
